import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ErrorAlert, SuccessAlert } from "@shared/ui/alerts";
import { InputController } from "@shared/ui/form";
import { useActivities } from "../hooks/useActivities";
import { useProjectIssues } from "../hooks/useProjectIssues";
import { useCreateTimeEntry } from "../hooks/useCreateTimeEntry";
import { buildMissingHourSchema } from "../schema/missingHourSchema";
import { buildMissingHourEntry } from "../lib/timeEntryMappers";
import { EntitySelect } from "./EntitySelect";

import type { MissingHourValues } from "../schema/missingHourSchema";
import type { CreateTimeEntryPayload, MissingHoursWeeklySummary } from "../api/timeEntryApi";

import type { TFunction } from "i18next";

export interface MissingHourDialogProps {
  handleClose: () => void;
  open: boolean;
  notLoading: () => void;
  loading: () => void;
  missingHour: MissingHoursWeeklySummary | null;
  reloadInitialMissingHour: () => void;
  t: TFunction;
}

export const MissingHourDialog = ({
  handleClose,
  open,
  notLoading,
  loading,
  missingHour,
  reloadInitialMissingHour,
  t,
}: MissingHourDialogProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<MissingHourValues>({ resolver: yupResolver(buildMissingHourSchema(t)) as never });

  const { data: activityList = [] } = useActivities(open);
  const { data: projectIssues } = useProjectIssues(missingHour?.projectId);
  const issueId = projectIssues?.[0]?.id;

  const createTimeEntry = useCreateTimeEntry();

  // Hallazgo nuevo (no en #2572): este TextField no está registrado en el form (ni
  // Controller ni `control.register`) y el schema no valida "project" — el bloque es
  // código muerto, siempre false. Se documenta y se preserva; no hay una regla de
  // validación real que "restaurar".
  // @ts-expect-error -- "project" no existe en MissingHourValues (ver comentario arriba)
  const projectError = errors.project;

  const onRegister = (values: MissingHourValues) => {
    handleClose();
    loading();
    const activity = activityList.find((item) => item.id === Number(values.activityId));
    // `missingHour` no es null acá: el form solo se renderiza cuando `missingHour !== null`
    // (ver JSX abajo). `buildMissingHourEntry` espera `projectId`/`days` no opcionales
    // (asume que el call site siempre los pobló, ver comentario en timeEntryMappers.ts);
    // el contrato generado los declara opcionales, de ahí el `as never`. `issueId` puede
    // ser undefined si el proyecto no tiene issues (comportamiento preexistente sin
    // guard, ver #2572).
    const entry = buildMissingHourEntry(values, {
      missingHour: missingHour as never,
      activity,
      issueId: issueId!,
    });

    // `entry.hours` sale de `day.missingHours` (opcional en el contrato); comportamiento
    // preexistente sin guard (ver #2572), no se agrega uno nuevo acá.
    createTimeEntry.mutate(entry as CreateTimeEntryPayload, {
      onSuccess: () => {
        notLoading();
        SuccessAlert(t("loadHours"), t("doneSuccessfully", { a: "a" })).then(() =>
          reloadInitialMissingHour()
        );
      },
      onError: (error) => {
        notLoading();
        ErrorAlert(t("errorTitle"), error.response?.data?.message ?? t("internalError")).then();
      },
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {missingHour !== null && (
        <form onSubmit={handleSubmit(onRegister)}>
          <DialogTitle>{t("loadRemainingHours")}</DialogTitle>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid size={12}>
                <FormControl fullWidth>
                  <TextField
                    label={t("project")}
                    margin="dense"
                    value={missingHour.projectName}
                    fullWidth
                    name="project"
                    id="formatted-numberformat-input"
                    variant="outlined"
                    slotProps={{ input: { readOnly: true } }}
                  />
                  {projectError && (
                    <span style={{ fontSize: 12, color: "red" }}>{projectError.message}</span>
                  )}
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth>
                  <EntitySelect
                    control={control}
                    defaultValue={[]}
                    label={t("activities")}
                    name="activityId"
                    listValue={activityList}
                  />
                  {errors.activityId && (
                    <span style={{ fontSize: 12, color: "red" }}>{errors.activityId.message}</span>
                  )}
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth>
                  <Controller
                    name="spentOn"
                    control={control}
                    defaultValue={"" as never}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">{t("date")}</InputLabel>
                        <Select {...field}>
                          {missingHour.days?.map(({ date, missingHours }, index) => (
                            <MenuItem value={date} key={index}>
                              {date} - {t("hoursRemaining")}: {missingHours}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  {errors.spentOn && (
                    <span style={{ fontSize: 12, color: "red" }}>{errors.spentOn.message}</span>
                  )}
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputController
                    name="comment"
                    control={control}
                    type="text"
                    defaultValue=""
                    label={t("description")}
                  />
                  {errors.comment && (
                    <span style={{ fontSize: 12, color: "red" }}>{errors.comment.message}</span>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t("cancel")}</Button>
            <Button type="submit">{t("load")}</Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
